"use client";
import AppIcon from "../../../App/AppIcon";
import { useState, useEffect, useRef } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import "./style.css";

interface Props {
  name: string;
  chooseLabel?: string;
  removeLabel?: string;
  changeLabel?: string;
  control: Control<any, any>;
  maxSize?: number;
  errorProp?: string | FieldErrors;
  disabled?: boolean;
  proportions?: string;
  defaultValue?: File | string;
  containerClassName?: string;
  inputClassName?: string;
  previewContainerClassName?: string;
  iconClassName?: string;
  proportionsClassName?: string;
}

const ControlledImage = ({
  name,
  chooseLabel = "Escolher Foto",
  removeLabel = "Remover",
  changeLabel = "Trocar Foto",
  control,
  maxSize = 5,
  disabled,
  errorProp,
  proportions = "500x500",
  defaultValue,
  containerClassName = "",
  inputClassName = "",
  previewContainerClassName = "",
  iconClassName = "",
  proportionsClassName = "",
}: Props) => {
  const [imagePreview, setImagePreview] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const getErrorMessage = (): string | undefined => {
    if (!errorProp) return undefined;

    if (typeof errorProp === "string") {
      return errorProp;
    }

    // Navega pela estrutura aninhada de erros usando o caminho do name
    // Ex: "address.street" -> error.address.street
    if (typeof errorProp === "object") {
      const nameParts = name.split(".");
      let currentError: any = errorProp;

      for (const part of nameParts) {
        if (currentError && typeof currentError === "object" && part in currentError) {
          currentError = currentError[part];
        } else {
          return undefined;
        }
      }

      return currentError?.message as string | undefined;
    }

    return undefined;
  };

  const errorMessage = getErrorMessage();

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>, onChange: (value: Blob | null) => void) => {
    const file = e.target.files ? e.target.files[0] : null;

    if (file) {
      if (file.size > maxSize * 1024 * 1024) {
        setError(`O arquivo não pode exceder ${maxSize}MB.`);
        setImagePreview(undefined);
        onChange(null);
      } else {
        setError(undefined);
        const previewURL = URL.createObjectURL(file);
        setImagePreview(previewURL);
        onChange(file);
      }
    }
  };

  const handleRemove = (onChange: any) => {
    setImagePreview(undefined);
    onChange(undefined);
  };

  useEffect(() => {
    if (defaultValue) {
      typeof defaultValue === "string" ? setImagePreview(defaultValue) : setImagePreview(URL.createObjectURL(defaultValue));
    }
  }, [defaultValue]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <div className={`controlled-image-container ${containerClassName}`}>
          <div className={`controlled-image-preview ${disabled ? "disabled" : ""} ${previewContainerClassName}`}>
            {imagePreview ? (
              <img src={imagePreview} alt="Preview" />
            ) : (
              <div className={`controlled-image-placeholder ${disabled ? "disabled" : ""}`} onClick={disabled ? () => {} : handleClick}>
                <AppIcon icon="solar:camera-bold" width={80} className={`controlled-image-icon ${iconClassName}`} />
                <p className={`controlled-image-text ${proportionsClassName}`}>{proportions}</p>
              </div>
            )}
            {errorMessage && <small className="controlled-image-error-prop">{errorMessage}</small>}
          </div>

          {error && <div className="controlled-image-error">{error}</div>}

          <div className="controlled-image-buttons">
            <button
              type="button"
              className={`controlled-image-button controlled-image-button-choose ${inputClassName} ${disabled ? "disabled" : ""}`}
              onClick={handleClick}
              disabled={disabled}
            >
              {value ? changeLabel : chooseLabel}
            </button>

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleUpload(e, onChange)}
              className="controlled-image-input"
              disabled={disabled}
            />

            {value && (
              <button
                type="button"
                className={`controlled-image-button controlled-image-button-remove ${disabled ? "disabled" : ""}`}
                onClick={() => handleRemove(onChange)}
                disabled={disabled}
              >
                {removeLabel}
              </button>
            )}
          </div>
        </div>
      )}
    />
  );
};

export default ControlledImage;
